/**
 * Browser lifecycle around the procedural engine: the AudioContext is only
 * created and resumed after a player gesture, everything is silent while
 * disabled, and nothing here ever throws into the game loop.
 */
import { RideAudioEngine } from './engine';
import type { AudioRideState, AudioTrack, RideCue } from './types';

type AudioContextCtor = new (options?: AudioContextOptions) => AudioContext;

export class RideSound {
  enabled = false;
  private ctx: AudioContext | null = null;
  private engine: RideAudioEngine | null = null;
  private track: AudioTrack | null = null;
  private timer: ReturnType<typeof setInterval> | null = null;
  private failed = false;
  private unlocking = false;
  private lifecycleBound = false;

  /** Turn audio on or off. Resolves `false` only when Web Audio is unavailable. */
  async enable(on: boolean): Promise<boolean> {
    this.enabled = on;
    if (!on) {
      this.stopTimer();
      this.safe(() => this.engine?.setActive(false));
      const ctx = this.ctx;
      if (ctx) setTimeout(() => { if (!this.enabled && ctx.state === 'running') ctx.suspend().catch(() => {}); }, 400);
      return true;
    }
    const ctx = this.context();
    if (!ctx) return false;
    const resumed = this.resume(ctx); // call resume() inside the gesture, before heavier set-up
    if (!this.engine) {
      try {
        // Fewer rival voices on low-core devices; HRTF itself is cheap when positions are not automated.
        const cores = typeof navigator !== 'undefined' ? navigator.hardwareConcurrency || 4 : 4;
        this.engine = new RideAudioEngine(ctx, { hrtf: true, rivalVoices: cores <= 4 ? 3 : 4 });
        if (this.track) this.engine.setBiome(this.track);
        this.prewarm();
      } catch {
        this.failed = true; this.engine = null;
        return false;
      }
    }
    this.bindLifecycle();
    this.safe(() => this.engine?.setActive(true));
    this.startTimer();
    await resumed;
    return true;
  }
  /** Per-frame ride state; `null` for menus, pause and results (rolling goes silent). */
  update(state: AudioRideState | null, track?: AudioTrack) {
    if (track) this.track = track;
    if (this.enabled && this.engine) this.safe(() => this.engine!.update(state, track));
  }
  /** Switch biome ambience, reverb and bike acoustics for a track or arena. */
  setBiome(track: AudioTrack) {
    this.track = track;
    if (this.engine) this.safe(() => this.engine!.setBiome(track));
  }
  cue(kind: RideCue) {
    if (this.enabled && this.engine) this.safe(() => this.engine!.cue(kind));
  }
  /** Countdown seconds remaining (ticks on 3, 2, 1). */
  countdown(remaining: number) {
    if (this.enabled && this.engine) this.safe(() => this.engine!.countdown(remaining));
  }

  private context(): AudioContext | null {
    if (this.ctx) return this.ctx;
    if (this.failed) return null;
    const scope = globalThis as unknown as { AudioContext?: AudioContextCtor; webkitAudioContext?: AudioContextCtor };
    const Ctor = scope.AudioContext ?? scope.webkitAudioContext;
    if (!Ctor) { this.failed = true; return null; }
    try { this.ctx = new Ctor({ latencyHint: 'interactive' }); }
    catch { try { this.ctx = new Ctor(); } catch { this.failed = true; return null; } }
    return this.ctx;
  }
  private async resume(ctx: AudioContext) {
    if (ctx.state === 'running') return;
    try { await Promise.race([ctx.resume(), new Promise(resolve => setTimeout(resolve, 900))]); } catch { /* blocked: wait for a gesture */ }
    if ((ctx.state as string) !== 'running') this.unlockOnGesture(ctx);
  }
  /** Some browsers (iOS) only resume inside a later tap or key press. */
  private unlockOnGesture(ctx: AudioContext) {
    if (this.unlocking || typeof window === 'undefined') return;
    this.unlocking = true;
    const events = ['pointerdown', 'touchend', 'keydown'] as const;
    const unlock = () => {
      if (!this.enabled) return;
      ctx.resume().then(() => {
        if (ctx.state !== 'running') return;
        events.forEach(e => window.removeEventListener(e, unlock, true));
        this.unlocking = false;
      }).catch(() => {});
    };
    events.forEach(e => window.addEventListener(e, unlock, true));
  }
  private bindLifecycle() {
    if (this.lifecycleBound || typeof document === 'undefined') return;
    this.lifecycleBound = true;
    document.addEventListener('visibilitychange', () => {
      const ctx = this.ctx;
      if (!ctx) return;
      if (document.hidden) {
        this.safe(() => this.engine?.update(null));
        if (ctx.state === 'running') ctx.suspend().catch(() => {});
      } else if (this.enabled) void this.resume(ctx);
    });
  }
  private startTimer() {
    if (this.timer !== null) return;
    this.timer = setInterval(() => this.safe(() => this.engine?.pump()), 100);
  }
  private stopTimer() {
    if (this.timer !== null) clearInterval(this.timer);
    this.timer = null;
  }
  /** Render deferred one-shot buffers in small idle slices. */
  private prewarm() {
    const step = () => { const engine = this.engine; if (engine && engine.kit.prewarm(6) > 0) setTimeout(step, 45); };
    setTimeout(step, 80);
  }
  private safe(fn: () => void) {
    try { fn(); } catch { /* audio must never break the ride */ }
  }
}
